import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Zap, Loader, AlertCircle } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
    addDoc,
    collection,
    getDocs,
    query,
    where,
    onSnapshot,
    doc,
    setDoc,
} from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import {
    getSubscriptionsCollectionPath,
    getCheckoutSessionsCollectionPath,
    STRIPE_ENABLED,
} from '../util/constants';

const PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_ID || '';

const Upgrade = () => {
    const { currentUser, profile, refreshProfile } = useAuth();
    const [loading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [agbAccepted, setAgbAccepted] = useState(false);
    const [datenschutzAccepted, setDatenschutzAccepted] = useState(false);
    const [expressConsentAccepted, setExpressConsentAccepted] = useState(false);
    const [checkboxError, setCheckboxError] = useState('');

    const [hasActiveSub, setHasActiveSub] = useState(false);

    useEffect(() => {
        if (!currentUser || currentUser.isAnonymous) return;
        const subsRef = collection(db, getSubscriptionsCollectionPath(currentUser.uid));
        const q = query(subsRef, where('status', 'in', ['active', 'trialing']));
        const unsub = onSnapshot(q, (snap) => {
            setHasActiveSub(!snap.empty);
        });
        return unsub;
    }, [currentUser]);

    const canceled = searchParams.get('canceled');
    const success = searchParams.get('success');

    if (currentUser?.isAnonymous) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 flex items-center justify-center p-4 pt-24">
                <div className="bg-white/70 backdrop-blur-lg rounded-3xl p-8 text-center max-w-md">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">💳 Pro-Abo</h1>
                    <p className="text-gray-600 mb-4">
                        Du nutzt Studytino ohne Konto. Um ein Pro-Abo abzuschließen, musst du zuerst ein Konto mit E-Mail und Passwort erstellen.
                    </p>
                    <Link to="/register" className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition">
                        Konto erstellen
                    </Link>
                </div>
            </div>
        );
    }

    if (profile?.tier === 'pro' || hasActiveSub) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 flex items-center justify-center p-4 pt-24">
                <div className="bg-white/70 backdrop-blur-lg rounded-3xl p-8 text-center max-w-md">
                    <div className="text-5xl mb-4">🎉</div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        {success ? 'Upgrade erfolgreich!' : 'Du hast bereits ein aktives Abo!'}
                    </h1>
                    <p className="text-gray-600 mb-6">
                        {success
                            ? 'Dein Konto wurde auf Pro aktualisiert. Viel Spaß mit den erweiterten Funktionen!'
                            : 'Genieße alle Vorteile deines Pro-Abos.'}
                    </p>
                    <button
                        onClick={() => navigate('/lernsets')}
                        className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
                    >
                        Zu meinen Lernsets
                    </button>
                </div>
            </div>
        );
    }

    const validateCheckboxes = (): boolean => {
        if (!agbAccepted) {
            setCheckboxError('Bitte akzeptiere die Allgemeinen Geschäftsbedingungen.');
            return false;
        }
        if (!datenschutzAccepted) {
            setCheckboxError('Bitte bestätige die Kenntnisnahme der Datenschutzerklärung.');
            return false;
        }
        if (!expressConsentAccepted) {
            setCheckboxError('Bitte bestätige die Zustimmung zum vorzeitigen Leistungsbeginn.');
            return false;
        }
        setCheckboxError('');
        return true;
    };

    const saveConsents = async () => {
        if (!currentUser) return;
        const consentRef = doc(db, 'users', currentUser.uid, 'consents', 'legal');
        await setDoc(consentRef, {
            agb: {
                accepted: agbAccepted,
                version: '1.0',
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
            },
            datenschutz: {
                accepted: datenschutzAccepted,
                version: '1.0',
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
            },
            expressConsent: {
                accepted: expressConsentAccepted,
                version: '1.0',
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
            },
        });
    };

    const handleUpgrade = async () => {
        if (!STRIPE_ENABLED) {
            alert('Abos sind derzeit nicht verfügbar. Bitte versuche es später erneut.');
            return;
        }

        if (!validateCheckboxes()) return;
        if (!currentUser) return;
        setLoading(true);

        try {
            await saveConsents();

            const subsRef = collection(db, getSubscriptionsCollectionPath(currentUser.uid));
            const q = query(subsRef, where('status', 'in', ['active', 'trialing']));
            const querySnap = await getDocs(q);
            if (!querySnap.empty) {
                await refreshProfile();
                alert('Du hast bereits ein aktives Abo.');
                setLoading(false);
                return;
            }

            const ensureCustomer = httpsCallable(functions, 'ensureStripeCustomer');
            await ensureCustomer();

            const docRef = await addDoc(
                collection(db, getCheckoutSessionsCollectionPath(currentUser.uid)),
                {
                    price: PRICE_ID,
                    success_url: `${window.location.origin}/upgrade?session_id={CHECKOUT_SESSION_ID}&success=true`,
                    cancel_url: `${window.location.origin}/upgrade?canceled=true`,
                }
            );

            const unsubscribe = onSnapshot(docRef, (snap) => {
                const data = snap.data();
                if (data?.error) {
                    console.error('❌ Stripe Fehler:', data.error);
                    alert(`Stripe Fehler: ${data.error.message}`);
                    unsubscribe();
                    setLoading(false);
                }
                if (data?.url) {
                    window.location.href = data.url;
                    unsubscribe();
                }
            });
        } catch (err: any) {
            console.error('❌ Upgrade-Fehler:', err);
            alert('Fehler beim Starten des Checkouts: ' + (err.message || 'Unbekannter Fehler. Bitte versuche es später erneut.'));
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24">
            <div className="max-w-4xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 md:p-12"
                >
                    <div className="text-center mb-10">
                        <Zap className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Upgrade auf Pro</h1>
                        <p className="text-gray-600 text-lg">Erhalte höhere Nutzungslimits und mehr Funktionen</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 mb-12">
                        <div className="bg-white/50 rounded-2xl p-6 border border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-800 mb-4">Free</h3>
                            <p className="text-3xl font-bold text-gray-900 mb-4">0 €</p>
                            <ul className="space-y-3">
                                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-green-500" /> 3 Generierungen / Tag</li>
                                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-green-500" /> Max. 100 Karten</li>
                                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-green-500" /> Upload bis 3 MB</li>
                            </ul>
                        </div>

                        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border-2 border-indigo-300 shadow-lg">
                            <h3 className="text-xl font-semibold text-indigo-800 mb-4">Pro</h3>
                            <p className="text-3xl font-bold text-gray-900 mb-4">6,99 € <span className="text-sm text-gray-500">/ Monat</span></p>
                            <ul className="space-y-3">
                                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-green-500" /> 100 Generierungen / Tag</li>
                                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-green-500" /> Max. 1500 Karten</li>
                                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-green-500" /> Upload bis 10 MB</li>
                                <li className="flex items-center gap-2"><Check className="w-5 h-5 text-green-500" /> Smart-Lernmodus</li>
                            </ul>
                        </div>
                    </div>

                    {checkboxError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-6 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">{checkboxError}</span>
                        </div>
                    )}

                    {canceled && (
                        <div className="bg-yellow-50 text-yellow-800 p-4 rounded-xl mb-6">
                            Zahlung abgebrochen – versuch es nochmal.
                        </div>
                    )}
                    {success && (
                        <div className="bg-green-50 text-green-800 p-4 rounded-xl mb-6">
                            ✅ Upgrade erfolgreich! Du bist jetzt Pro.
                        </div>
                    )}

                    <div className="bg-gray-50 rounded-2xl p-6 mb-6 border border-gray-200 space-y-4">
                        <h4 className="font-semibold text-gray-800 text-sm uppercase tracking-wide">Rechtliche Hinweise</h4>

                        <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer hover:text-gray-900 transition-colors">
                            <input
                                type="checkbox"
                                checked={agbAccepted}
                                onChange={(e) => setAgbAccepted(e.target.checked)}
                                className="mt-1 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
                            />
                            <span>
                                Ich akzeptiere die <Link to="/agb" className="text-indigo-600 hover:underline font-medium" target="_blank">Allgemeinen Geschäftsbedingungen</Link>.
                            </span>
                        </label>

                        <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer hover:text-gray-900 transition-colors">
                            <input
                                type="checkbox"
                                checked={datenschutzAccepted}
                                onChange={(e) => setDatenschutzAccepted(e.target.checked)}
                                className="mt-1 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
                            />
                            <span>
                                Ich habe die <Link to="/datenschutz" className="text-indigo-600 hover:underline font-medium" target="_blank">Datenschutzerklärung</Link> zur Kenntnis genommen.
                            </span>
                        </label>

                        <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer hover:text-gray-900 transition-colors">
                            <input
                                type="checkbox"
                                checked={expressConsentAccepted}
                                onChange={(e) => setExpressConsentAccepted(e.target.checked)}
                                className="mt-1 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
                            />
                            <span>
                                Ich verlange ausdrücklich, dass <strong>Studytino Pro</strong> bereits vor Ablauf der Widerrufsfrist bereitgestellt wird. Mir ist bekannt, dass mein Widerrufsrecht bei vollständiger Vertragserfüllung{' '}
                                <Link to="/widerruf" className="text-indigo-600 hover:underline font-medium" target="_blank">
                                    unter den gesetzlichen Voraussetzungen erlischt
                                </Link>
                                .
                            </span>
                        </label>
                    </div>

                    {!STRIPE_ENABLED && (
                        <div className="bg-yellow-50 text-yellow-800 p-4 rounded-xl mb-6">
                            ⚠️ Abos sind aktuell noch nicht freigeschaltet. Du kannst die App im Free‑Modus nutzen.
                        </div>
                    )}

                    <button
                        onClick={handleUpgrade}
                        disabled={loading || !STRIPE_ENABLED}
                        className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-all ${
                            loading || !STRIPE_ENABLED
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg'
                        }`}
                    >
                        {loading ? <Loader className="animate-spin w-5 h-5" /> : <Zap className="w-5 h-5" />}
                        {loading ? 'Wird vorbereitet...' : !STRIPE_ENABLED ? 'Derzeit nicht verfügbar' : 'Jetzt kostenpflichtig abonnieren'}
                    </button>
                </motion.div>
            </div>
        </div>
    );
};

export default Upgrade;